/**
 * Historique des interactions d'un chantier : consigner, lire, retirer (R1, R2, R3).
 *
 * ⚠️ L'IDENTIFIANT EST CELUI DU CLIENT RENDU PAR LA FICHE, JAMAIS LE PARAMETRE D'URL.
 * Sur `/sav/chantier/<ref>`, ce parametre est la reference du chantier : l'ajout partait
 * vers `/distributeur/clients/CH-SYDUXB/interactions` et le serveur repondait 404
 * « Client introuvable » (recette du 26/10). Lecture, ajout et suppression passent donc
 * tous par `clientId`, que la page tire de la reponse de fiche.
 *
 * Une fiche sans client (`id` vide) ne peut rien consigner : le formulaire est ferme et
 * l'ecran le dit, plutot que de laisser partir un appel voue au 404.
 *
 * Tour 2 (relecture ux) : `onChange` ne relit QUE l'historique (la page ne repasse plus
 * par son spinner), et le focus revient au champ « Contenu » apres un ajout ou une
 * suppression : au clavier, on enchaine les saisies sans retraverser la fiche.
 */
import { useRef, useState } from 'react';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Badge } from '@/shared/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Separator } from '@/shared/ui/separator';
import { MessageSquare, Phone, Mail, Wrench, User, Send, Trash2, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate } from '@/features/distributeur/dates';
import { messageDeRefus } from '@/features/distributeur/refus';

// EXACTEMENT le vocabulaire du serveur (`TYPES_INTERACTION`, `chantiers.py`), dans
// l'ordre du menu. Un type propose ici et refuse la-bas vaut un 400 a l'utilisateur.
export const TYPES_INTERACTION = [
  { valeur: 'commentaire', libelle: 'Commentaire', Icone: MessageSquare },
  { valeur: 'appel', libelle: 'Appel téléphonique', Icone: Phone },
  { valeur: 'email', libelle: 'E-mail', Icone: Mail },
  { valeur: 'sav', libelle: 'Intervention SAV', Icone: Wrench },
  { valeur: 'visite', libelle: 'Visite sur site', Icone: User },
  { valeur: 'autre', libelle: 'Autre', Icone: MoreHorizontal },
];
const PAR_VALEUR = Object.fromEntries(TYPES_INTERACTION.map((t) => [t.valeur, t]));

export const SANS_CLIENT =
  "Ce chantier n'est rattaché à aucun client : impossible d'y consigner une interaction.";

export const HISTORIQUE_INDISPONIBLE =
  "Historique indisponible : il n'a pas pu être lu. Réessayez avant de saisir à nouveau.";

export default function PanneauInteractions({
  clientId, interactions, historiqueIndisponible, user, onChange,
}) {
  const champContenu = useRef(null);
  const [type, setType] = useState('commentaire');
  const [contenu, setContenu] = useState('');
  const [sending, setSending] = useState(false);
  const ferme = !clientId;
  const base = `/distributeur/clients/${encodeURIComponent(clientId || '')}/interactions`;

  const submit = async (e) => {
    e.preventDefault();
    if (ferme || !contenu.trim()) return;
    setSending(true);
    try {
      await api.post(base, { type, contenu: contenu.trim() });
      setContenu('');
      setType('commentaire');
      toast.success('Interaction ajoutée');
      await onChange?.();
      champContenu.current?.focus();
    } catch (err) {
      toast.error(messageDeRefus(err, 'Erreur'));
    }
    setSending(false);
  };

  const remove = async (id) => {
    if (ferme || !window.confirm('Supprimer cette interaction ?')) return;
    try {
      await api.delete(`${base}/${encodeURIComponent(id)}`);
      toast.success('Supprimée');
      await onChange?.();
      // La ligne supprimee (et son bouton) n'existe plus : le formulaire est l'endroit
      // sense ou reprendre, plutot que le haut du document.
      champContenu.current?.focus();
    } catch (err) {
      toast.error(messageDeRefus(err, 'Erreur'));
    }
  };

  return (
    <div className="rounded-lg border border-neutral-200/80 bg-white p-5 space-y-4" data-testid="interactions-panel">
      <div>
        <h2 className="text-[14px] font-semibold text-neutral-900 flex items-center gap-2">
          <MessageSquare className="w-4 h-4" /> Historique des interactions
        </h2>
        <p className="text-[11px] text-neutral-400 mt-0.5">Commentaires, appels, SAV, e-mails — visible par tous les distributeurs et admins de votre réseau.</p>
      </div>

      {ferme && (
        <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2" data-testid="interactions-sans-client">
          {SANS_CLIENT}
        </p>
      )}

      <form onSubmit={submit} className="p-3 rounded-md bg-neutral-50/60 border border-neutral-100" data-testid="interaction-form">
        <fieldset disabled={ferme} className="flex gap-3 flex-wrap">
          <div className="w-52">
            <Label htmlFor="interaction-type" className="text-[11px] text-neutral-500">Type</Label>
            <Select value={type} onValueChange={setType} disabled={ferme}>
              <SelectTrigger id="interaction-type" className="h-9" data-testid="interaction-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPES_INTERACTION.map((t) => (
                  <SelectItem key={t.valeur} value={t.valeur}>{t.libelle}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[12rem]">
            <Label htmlFor="interaction-contenu" className="text-[11px] text-neutral-500">Contenu</Label>
            <Input
              id="interaction-contenu"
              ref={champContenu}
              value={contenu}
              onChange={(e) => setContenu(e.target.value)}
              placeholder="Ex: client rappellera demain 10 h — vérifier serveur Hoymiles"
              className="h-9"
              data-testid="interaction-content-input"
              maxLength={4000}
            />
          </div>
          <div className="self-end">
            <Button type="submit" size="sm" disabled={ferme || sending || !contenu.trim()} data-testid="interaction-submit-btn">
              <Send className="w-3.5 h-3.5 mr-1" /> {sending ? 'Envoi…' : 'Ajouter'}
            </Button>
          </div>
        </fieldset>
      </form>

      {historiqueIndisponible ? (
        <div className="text-[12px] text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="interactions-indisponibles">
          <span>{HISTORIQUE_INDISPONIBLE}</span>
          <Button type="button" size="sm" variant="outline" onClick={() => onChange?.()}>Réessayer</Button>
        </div>
      ) : interactions.length === 0 ? (
        <p className="text-[12px] text-neutral-400 text-center py-6" data-testid="interactions-empty">
          Aucune interaction pour le moment.
        </p>
      ) : (
        <div className="space-y-3" data-testid="interactions-timeline">
          {interactions.map((it) => {
            const Icon = PAR_VALEUR[it.type]?.Icone || MessageSquare;
            const canDelete = !ferme && (user?.role === 'admin' || it.author_id === user?.id);
            return (
              <div key={it.id} className="flex gap-3 items-start" data-testid={`interaction-row-${it.id}`}>
                <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-neutral-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[12px] font-medium text-neutral-800">{PAR_VALEUR[it.type]?.libelle || it.type}</span>
                    <Badge variant="outline" className="text-[10px] py-0 px-1.5">{it.author_role}</Badge>
                    <span className="text-[11px] text-neutral-500">{it.author_nom}</span>
                    <Separator orientation="vertical" className="h-3" />
                    <span className="text-[11px] text-neutral-400">{fmtDate(it.created_at)}</span>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => remove(it.id)}
                        className="ml-auto p-1 rounded hover:bg-red-50 text-neutral-300 hover:text-red-500"
                        aria-label="Supprimer l'interaction"
                        data-testid={`interaction-delete-${it.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <p className="text-[13px] text-neutral-800 mt-0.5 whitespace-pre-wrap break-words">{it.contenu}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
